<template>
  <div class="verify-definitive-container">
    <!-- Header -->
    <b-card class="mb-2">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h4 class="mb-0">
            <feather-icon
              icon="CheckSquareIcon"
              size="24"
              class="mr-1"
            />
            Vérification Manager
          </h4>
          <small class="text-muted">Vérifiez les bons de retour créés par le support</small>
        </div>
        <div class="d-flex align-items-center">
          <b-badge
            variant="light-danger"
            class="mr-1"
          >
            {{ scannedDefinitive.length }} Retour Définitif
          </b-badge>
          <b-badge
            variant="light-warning"
            class="mr-1"
          >
            {{ scannedRelance.length }} Relance
          </b-badge>
          <b-badge variant="light-primary">
            {{ unverifiedVouchers.length }} bons à vérifier
          </b-badge>
        </div>
      </div>
    </b-card>

    <!-- Scanner Section -->
    <b-card class="mb-2 scanner-card">
      <b-row align-v="center">
        <b-col
          cols="12"
          md="8"
        >
          <b-input-group size="lg">
            <b-input-group-prepend is-text>
              <feather-icon
                icon="MaximizeIcon"
                size="20"
              />
            </b-input-group-prepend>
            <b-form-input
              ref="scannerInput"
              v-model="scanRef"
              placeholder="📦 Scannez ou saisissez la référence du colis..."
              class="scanner-input"
              autofocus
              @keyup.enter="scanOrder"
            />
            <b-input-group-append>
              <b-button
                variant="primary"
                @click="scanOrder"
              >
                <feather-icon
                  icon="SearchIcon"
                  class="mr-50"
                />
                Scanner
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
          class="text-right mt-1 mt-md-0"
        >
          <b-button
            v-if="scannedDefinitive.length > 0 || scannedRelance.length > 0"
            variant="outline-secondary"
            size="sm"
            @click="clearScannedOrders"
          >
            <feather-icon
              icon="Trash2Icon"
              size="14"
              class="mr-50"
            />
            Effacer scans
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Scanned Orders - Two Tables Side by Side -->
    <b-row
      v-if="scannedDefinitive.length > 0 || scannedRelance.length > 0"
      class="mb-2"
    >
      <!-- Scanned Retour Définitif -->
      <b-col
        cols="12"
        lg="6"
      >
        <b-card class="scanned-card definitive-card h-100">
          <template #header>
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0 text-danger">
                <feather-icon
                  icon="XCircleIcon"
                  size="18"
                  class="mr-50"
                />
                Retour Définitif
                <b-badge
                  variant="danger"
                  pill
                  class="ml-50"
                >
                  {{ scannedDefinitive.length }}
                </b-badge>
              </h5>
            </div>
          </template>
          <div
            v-if="scannedDefinitive.length === 0"
            class="text-center py-3 text-muted"
          >
            <feather-icon
              icon="InboxIcon"
              size="32"
              class="mb-1"
            />
            <p>Aucun colis scanné</p>
          </div>
          <vue-good-table
            v-else
            :columns="scannedTableColumns"
            :rows="scannedDefinitive"
            :pagination-options="{ enabled: false }"
            style-class="vgt-table striped condensed"
          >
            <template
              slot="table-row"
              slot-scope="props"
            >
              <span v-if="props.column.field === 'order_id'">
                <b-badge variant="light-dark">
                  {{ props.row.order_id }}
                </b-badge>
              </span>
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>
        </b-card>
      </b-col>

      <!-- Scanned Relance -->
      <b-col
        cols="12"
        lg="6"
      >
        <b-card class="scanned-card relance-card h-100">
          <template #header>
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0 text-warning">
                <feather-icon
                  icon="RefreshCwIcon"
                  size="18"
                  class="mr-50"
                />
                Relance
                <b-badge
                  variant="warning"
                  pill
                  class="ml-50"
                >
                  {{ scannedRelance.length }}
                </b-badge>
              </h5>
            </div>
          </template>
          <div
            v-if="scannedRelance.length === 0"
            class="text-center py-3 text-muted"
          >
            <feather-icon
              icon="InboxIcon"
              size="32"
              class="mb-1"
            />
            <p>Aucun colis scanné</p>
          </div>
          <vue-good-table
            v-else
            :columns="scannedTableColumns"
            :rows="scannedRelance"
            :pagination-options="{ enabled: false }"
            style-class="vgt-table striped condensed"
          >
            <template
              slot="table-row"
              slot-scope="props"
            >
              <span v-if="props.column.field === 'order_id'">
                <b-badge variant="light-dark">
                  {{ props.row.order_id }}
                </b-badge>
              </span>
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>
        </b-card>
      </b-col>
    </b-row>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
      <p class="mt-1">
        Chargement...
      </p>
    </div>

    <!-- No Data -->
    <b-card
      v-else-if="!isLoading && unverifiedVouchers.length === 0"
      class="text-center py-3"
    >
      <feather-icon
        icon="CheckCircleIcon"
        size="48"
        class="text-success mb-1"
      />
      <h5>Aucun bon à vérifier</h5>
      <p class="text-muted">
        Tous les bons de retour ont été vérifiés.
      </p>
    </b-card>

    <!-- Unverified Vouchers - Orders Tables Side by Side -->
    <template v-else>
      <b-row>
        <!-- Retour Définitif Section -->
        <b-col
          cols="12"
          lg="6"
        >
          <b-card class="order-card definitive-card">
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0 text-danger">
                  <feather-icon
                    icon="XCircleIcon"
                    size="18"
                    class="mr-50"
                  />
                  Retour Définitif
                  <b-badge
                    variant="danger"
                    pill
                    class="ml-50"
                  >
                    {{ definitiveOrdersCount }}
                  </b-badge>
                </h5>
                <b-button
                  v-if="definitiveVouchers.length > 0"
                  variant="outline-danger"
                  size="sm"
                  :disabled="isVerifying"
                  @click="verifyAllByType(22)"
                >
                  <feather-icon
                    icon="CheckIcon"
                    size="14"
                    class="mr-50"
                  />
                  Vérifier
                </b-button>
              </div>
            </template>
            <div
              v-if="definitiveOrders.length === 0"
              class="text-center py-3 text-muted"
            >
              <feather-icon
                icon="InboxIcon"
                size="32"
                class="mb-1"
              />
              <p>Aucun colis en retour définitif</p>
            </div>
            <vue-good-table
              v-else
              :columns="orderColumns"
              :rows="definitiveOrders"
              :pagination-options="{ enabled: true, perPage: 10 }"
              style-class="vgt-table striped"
            >
              <template
                slot="table-row"
                slot-scope="props"
              >
                <span v-if="props.column.field === 'id'">
                  <b-badge variant="light-dark">
                    {{ props.row.id }}
                  </b-badge>
                </span>
                <span v-else-if="props.column.field === 'return_number'">
                  <b-badge
                    pill
                    :variant="props.row.return_number > 2 ? 'light-danger' : 'light-info'"
                  >
                    {{ props.row.return_number || 0 }}
                  </b-badge>
                </span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </b-card>
        </b-col>

        <!-- Relance Section -->
        <b-col
          cols="12"
          lg="6"
        >
          <b-card class="order-card relance-card">
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0 text-warning">
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="18"
                    class="mr-50"
                  />
                  Relance
                  <b-badge
                    variant="warning"
                    pill
                    class="ml-50"
                  >
                    {{ relanceOrdersCount }}
                  </b-badge>
                </h5>
                <b-button
                  v-if="relanceVouchers.length > 0"
                  variant="outline-warning"
                  size="sm"
                  :disabled="isVerifying"
                  @click="verifyAllByType(23)"
                >
                  <feather-icon
                    icon="CheckIcon"
                    size="14"
                    class="mr-50"
                  />
                  Vérifier
                </b-button>
              </div>
            </template>
            <div
              v-if="relanceOrders.length === 0"
              class="text-center py-3 text-muted"
            >
              <feather-icon
                icon="InboxIcon"
                size="32"
                class="mb-1"
              />
              <p>Aucun colis en relance</p>
            </div>
            <vue-good-table
              v-else
              :columns="orderColumns"
              :rows="relanceOrders"
              :pagination-options="{ enabled: true, perPage: 10 }"
              style-class="vgt-table striped"
            >
              <template
                slot="table-row"
                slot-scope="props"
              >
                <span v-if="props.column.field === 'id'">
                  <b-badge variant="light-dark">
                    {{ props.row.id }}
                  </b-badge>
                </span>
                <span v-else-if="props.column.field === 'return_number'">
                  <b-badge
                    pill
                    :variant="props.row.return_number > 2 ? 'light-danger' : 'light-info'"
                  >
                    {{ props.row.return_number || 0 }}
                  </b-badge>
                </span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </b-card>
        </b-col>
      </b-row>

      <!-- Verify All Button -->
      <b-card class="text-center mt-2">
        <b-button
          variant="success"
          size="lg"
          :disabled="isVerifying || unverifiedVouchers.length === 0"
          @click="verifyAll"
        >
          <b-spinner
            v-if="isVerifying"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="CheckCircleIcon"
            size="18"
            class="mr-50"
          />
          Vérifier tous les bons ({{ unverifiedVouchers.length }})
        </b-button>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BBadge, BSpinner,
  BFormInput, BInputGroup, BInputGroupPrepend, BInputGroupAppend,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from '@axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'VerifyDefinitive',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BBadge,
    BSpinner,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    VueGoodTable,
  },
  data() {
    return {
      isLoading: false,
      isVerifying: false,
      scanRef: '',
      unverifiedVouchers: [],
      scannedDefinitive: [],
      scannedRelance: [],
      orderColumns: [
        { label: 'BL', field: 'id', width: '90px' },
        {
          label: 'TNT', field: 'return_number', width: '60px', sortable: true,
        },
        { label: 'Client', field: 'name' },
        { label: 'Gouvernorat', field: 'governorate' },
      ],
      scannedTableColumns: [
        { label: 'BL', field: 'order_id', width: '100px' },
        { label: 'Client', field: 'client_name' },
        { label: 'Gouvernorat', field: 'governorate' },
      ],
    }
  },
  computed: {
    definitiveVouchers() {
      return this.unverifiedVouchers.filter(v => v.target_status === 22)
    },
    relanceVouchers() {
      return this.unverifiedVouchers.filter(v => v.target_status === 23)
    },
    definitiveOrders() {
      const orders = []
      this.definitiveVouchers.forEach(v => {
        if (v.orders && Array.isArray(v.orders)) {
          v.orders.forEach(o => {
            // Only add if not already scanned
            if (!this.scannedDefinitive.find(s => s.order_id === o.id)) {
              orders.push({
                ...o,
                voucher_id: v.id,
                governorate: o.governorate || '',
                delegation: o.delegation || '',
                supplier: v.created_by_name || '',
              })
            }
          })
        }
      })
      return orders
    },
    relanceOrders() {
      const orders = []
      this.relanceVouchers.forEach(v => {
        if (v.orders && Array.isArray(v.orders)) {
          v.orders.forEach(o => {
            // Only add if not already scanned
            if (!this.scannedRelance.find(s => s.order_id === o.id)) {
              orders.push({
                ...o,
                voucher_id: v.id,
                governorate: o.governorate || '',
                delegation: o.delegation || '',
                supplier: v.created_by_name || '',
              })
            }
          })
        }
      })
      return orders
    },
    definitiveOrdersCount() {
      return this.definitiveOrders.length
    },
    relanceOrdersCount() {
      return this.relanceOrders.length
    },
  },
  created() {
    this.loadUnverifiedVouchers()
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.scannerInput) {
        this.$refs.scannerInput.focus()
      }
    })
  },
  methods: {
    async loadUnverifiedVouchers() {
      this.isLoading = true
      try {
        const { data } = await axios.get('/api/return-vouchers/definitive/unverified/')
        this.unverifiedVouchers = data.results || data
      } catch (error) {
        this.showToast('danger', 'Erreur lors du chargement des bons')
        // eslint-disable-next-line no-console
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    async scanOrder() {
      if (!this.scanRef.trim()) return

      try {
        const { data } = await axios.get(`/api/return-vouchers/definitive/order-status/${this.scanRef.trim()}/`)

        if (data.found) {
          // Add to appropriate scanned list based on target_status
          if (data.target_status === 22) {
            // Check if not already scanned
            if (!this.scannedDefinitive.find(o => o.order_id === data.order_id)) {
              this.scannedDefinitive.unshift(data)
            }
            this.speakText('Retour définitif')
          } else if (data.target_status === 23) {
            if (!this.scannedRelance.find(o => o.order_id === data.order_id)) {
              this.scannedRelance.unshift(data)
            }
            this.speakText('Relance')
          } else {
            this.speakText('Colis non traité')
          }

          this.showToast('success', `${data.order_id}: ${data.action_type || 'Non traité'}`)
        } else {
          this.speakText('Colis non trouvé')
          this.showToast('warning', 'Colis non trouvé')
        }
      } catch (error) {
        this.speakText('Colis non trouvé')
        this.showToast('danger', 'Colis non trouvé')
      }

      this.scanRef = ''
      this.$nextTick(() => {
        if (this.$refs.scannerInput) {
          this.$refs.scannerInput.focus()
        }
      })
    },
    speakText(text) {
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text)
        utterance.lang = 'fr-FR'
        utterance.rate = 1.0
        utterance.pitch = 1.0
        window.speechSynthesis.speak(utterance)
      }
    },
    clearScannedOrders() {
      this.scannedDefinitive = []
      this.scannedRelance = []
    },
    async verifyVoucher(voucherId) {
      try {
        await axios.post('/api/return-vouchers/definitive/verify/', { voucher_id: voucherId })
        return true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error verifying voucher ${voucherId}:`, error)
        return false
      }
    },
    async verifyAllByType(targetStatus) {
      const vouchers = this.unverifiedVouchers.filter(v => v.target_status === targetStatus)
      if (vouchers.length === 0) return

      this.isVerifying = true

      const results = await Promise.all(
        vouchers.map(voucher => this.verifyVoucher(voucher.id)),
      )
      const successCount = results.filter(success => success).length

      this.showToast('success', `${successCount} bon(s) vérifié(s)`)
      await this.loadUnverifiedVouchers()
      this.isVerifying = false
    },
    async verifyAll() {
      if (this.unverifiedVouchers.length === 0) return

      this.isVerifying = true

      const results = await Promise.all(
        this.unverifiedVouchers.map(voucher => this.verifyVoucher(voucher.id)),
      )
      const successCount = results.filter(success => success).length

      this.showToast('success', `${successCount} bon(s) vérifié(s)`)
      await this.loadUnverifiedVouchers()
      this.isVerifying = false
    },
    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Vérification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-good-table.scss';

.verify-definitive-container {
  .scanner-card {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border: 2px dashed #dee2e6;

    .scanner-input {
      font-size: 1.1rem;
      font-weight: 500;
    }
  }

  .scanned-card, .order-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    &.definitive-card {
      border-top: 3px solid #ea5455;
    }

    &.relance-card {
      border-top: 3px solid #ff9f43;
    }
  }

  .h-100 {
    height: 100%;
  }
}
</style>
